import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { COUNTRY_CODES, findCountryByDial } from "../lib/countries";

const DEFAULT_DIAL = "+221";

/**
 * PhoneInput avec sélecteur d'indicatif pays (drapeaux emoji + dial code).
 * - Value émise : "+221 77 123 45 67" (dial + espace + numéro local)
 * - value initiale : accepte "+XX ..." (le préfixe est auto-détecté)
 * - Défaut : Sénégal (+221)
 */
export const PhoneInput = forwardRef(function PhoneInput(
  {
    value = "",
    onChange,
    prefix = DEFAULT_DIAL, // conservé pour retro-compatibilité
    placeholder = "77 123 45 67",
    testid,
    required = false,
    className = "",
  },
  ref,
) {
  // Auto-détecter l'indicatif depuis la value
  const detectDial = (v) => {
    if (!v) return prefix || DEFAULT_DIAL;
    const trimmed = v.trim();
    // On cherche le plus long dial code qui matche le début
    const sorted = [...COUNTRY_CODES].sort((a, b) => b.dial.length - a.dial.length);
    const match = sorted.find((c) => trimmed.startsWith(c.dial));
    return match ? match.dial : (prefix || DEFAULT_DIAL);
  };

  const [dial, setDial] = useState(detectDial(value));
  const [country, setCountry] = useState(findCountryByDial(detectDial(value)));

  const stripPrefix = (v, d) => {
    if (!v) return "";
    const trimmed = v.trim().replace(/\s+/g, " ");
    if (trimmed.startsWith(d)) return trimmed.slice(d.length).trim();
    return trimmed;
  };

  const [local, setLocal] = useState(stripPrefix(value, dial));

  useEffect(() => {
    const newDial = detectDial(value);
    setDial(newDial);
    setCountry(findCountryByDial(newDial));
    setLocal(stripPrefix(value, newDial));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const filteredCountries = useMemo(() => {
    if (!search.trim()) return COUNTRY_CODES;
    const q = search.toLowerCase().trim();
    return COUNTRY_CODES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dial.includes(q) ||
        c.code.toLowerCase().includes(q),
    );
  }, [search]);

  const handleSelectCountry = (c) => {
    setDial(c.dial);
    setCountry(c);
    setOpen(false);
    setSearch("");
    const full = local.trim() ? `${c.dial} ${local.trim()}` : "";
    onChange?.(full);
  };

  const handleLocalChange = (e) => {
    const raw = e.target.value.replace(/[^\d\s]/g, "");
    setLocal(raw);
    const full = raw.trim() ? `${dial} ${raw.trim()}` : "";
    onChange?.(full);
  };

  return (
    <div className={`relative flex items-stretch w-full rounded-md border border-[#E2E5F1] bg-white focus-within:border-[#1E3A8A] transition ${className}`}>
      {/* Sélecteur pays */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-2.5 bg-[#F1F3FB] border-r border-[#E2E5F1] rounded-l-md text-xs font-bold text-[#0F1A3D] hover:bg-[#E5E9F5] transition"
        data-testid={testid ? `${testid}-country` : "phone-country"}
      >
        <span className="text-lg leading-none">{country.flag}</span>
        <span className="tabular-nums">{dial}</span>
        <ChevronDown className="w-3 h-3 text-[#4B5775]" />
      </button>

      {/* Numéro local */}
      <input
        ref={ref}
        type="tel"
        inputMode="tel"
        value={local}
        onChange={handleLocalChange}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="flex-1 h-10 px-3 text-sm bg-transparent outline-none rounded-r-md"
        autoComplete="tel-national"
      />

      {/* Dropdown pays */}
      {open && (
        <div
          ref={dropdownRef}
          className="absolute z-50 top-full left-0 mt-1 w-80 max-h-72 overflow-hidden rounded-lg border border-[#E2E5F1] bg-white shadow-lg flex flex-col"
        >
          <div className="p-2 border-b border-[#E2E5F1] flex items-center gap-2 bg-[#F8FAFC]">
            <Search className="w-3.5 h-3.5 text-[#9CA3B5]" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un pays…"
              className="flex-1 h-7 text-xs bg-transparent outline-none"
              data-testid={testid ? `${testid}-country-search` : "phone-country-search"}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filteredCountries.length === 0 ? (
              <div className="p-4 text-xs text-[#9CA3B5] text-center">Aucun pays trouvé</div>
            ) : (
              filteredCountries.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => handleSelectCountry(c)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-[#F1F3FB] transition ${
                    c.code === country.code ? "bg-[#E5F0FF] font-bold" : ""
                  }`}
                >
                  <span className="text-base leading-none">{c.flag}</span>
                  <span className="flex-1 text-[#0F1A3D]">{c.name}</span>
                  <span className="tabular-nums text-[#4B5775] font-mono">{c.dial}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default PhoneInput;
